// Turns an Epson ePOS-Print document into flat display lines so the settings
// screen can show a ticket without a printer. Mirrors the backend builder's
// vocabulary: self-closing <text .../> sets sticky style, <text>…</text> carries
// content, <feed/> is blank space, <cut/> ends the ticket.

export interface PreviewSegment {
  text: string;
  bold: boolean;
}

export interface PreviewLine {
  segments: PreviewSegment[];
  align: 'left' | 'center' | 'right';
  /** ePOS width/height multipliers — height-only doubling keeps the column count. */
  width: number;
  height: number;
}

const TAG = /<([A-Za-z][\w-]*)([^>]*)>/g;
const ENTITY = /&(#x[0-9A-Fa-f]+|#\d+|amp|lt|gt|quot|apos);/g;

const NAMED: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
};

/** One left-to-right pass, so an escaped "&amp;#10;" stays literal text. */
function decodeEntities(s: string): string {
  return s.replace(ENTITY, (_, body: string) => {
    if (body.startsWith('#x')) return String.fromCodePoint(parseInt(body.slice(2), 16));
    if (body.startsWith('#')) return String.fromCodePoint(parseInt(body.slice(1), 10));
    return NAMED[body] ?? _;
  });
}

function attr(attrs: string, name: string): string | undefined {
  const m = new RegExp(`${name}="([^"]*)"`).exec(attrs);
  return m?.[1];
}

export function eposXmlToLines(xml: string): PreviewLine[] {
  const lines: PreviewLine[] = [];
  let align: PreviewLine['align'] = 'left';
  let bold = false;
  let width = 1;
  let height = 1;

  let segments: PreviewSegment[] = [];
  let lineAlign: PreviewLine['align'] = align;
  let lineWidth = width;
  let lineHeight = height;
  let started = false;

  const flush = () => {
    lines.push({
      segments,
      align: started ? lineAlign : align,
      width: started ? lineWidth : width,
      height: started ? lineHeight : height,
    });
    segments = [];
    started = false;
  };

  // A line takes the style of its first real content; an empty run must not
  // freeze the style of the line that follows it.
  const push = (text: string) => {
    if (!text) return;
    if (!started) {
      started = true;
      lineAlign = align;
      lineWidth = width;
      lineHeight = height;
    }
    lineWidth = Math.max(lineWidth, width);
    lineHeight = Math.max(lineHeight, height);
    segments.push({ text, bold });
  };

  TAG.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = TAG.exec(xml)) !== null) {
    const [raw, name, attrs] = m;
    if (name === 'cut') break;

    if (name === 'feed') {
      const n = Number(attr(attrs, 'line') ?? 0);
      for (let i = 0; i < n; i++) flush();
      continue;
    }

    if (name !== 'text') continue;

    if (attrs.trimEnd().endsWith('/')) {
      align = (attr(attrs, 'align') as PreviewLine['align']) ?? align;
      const em = attr(attrs, 'em');
      if (em) bold = em === 'true';
      const w = Number(attr(attrs, 'width') ?? 0);
      const h = Number(attr(attrs, 'height') ?? 0);
      if (w) width = w;
      if (h) height = h;
      continue;
    }

    // Content run: take everything up to its closing tag and resume after it.
    const start = m.index + raw.length;
    const end = xml.indexOf('</text>', start);
    const body = decodeEntities(xml.slice(start, end === -1 ? undefined : end));
    TAG.lastIndex = end === -1 ? xml.length : end + '</text>'.length;

    const parts = body.split('\n');
    parts.forEach((part, i) => {
      push(part);
      if (i < parts.length - 1) flush();
    });
  }

  // A trailing newline leaves an empty pending line — don't emit it.
  if (segments.length) flush();
  return lines;
}
